/**
 * useDashboardAgentTools — wires the CRM Dashboard (contacts table) into
 * FutureBot.
 *
 * Produces the useAgentPage() registration: live tool implementations bound
 * to the page's useContactsTable / useSmartLists instances (read through a
 * per-render ref so the stable closures never see stale state) plus a
 * getContext() snapshot with reference data (tags, contact types, sources,
 * users, smartlists).
 *
 * Tool names must match functions/FutureBot/toolsets.js (crm-dashboard).
 */

import { useCallback, useMemo, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../../utils/init-firebase';
import { useCrmSettings } from '../../../contexts/CrmSettingsContext';
import {
  waitForIdle,
  summarizeConditions,
  resolveSmartList,
  describeSmartList,
  canManageSmartList,
} from '../../../components/FutureBot/agentToolHelpers';

const DASHBOARD_SUGGESTIONS = [
  'Walk me through my morning briefing',
  'Show contacts tagged hot-lead added in the last 30 days',
  'Save this view as a smartlist',
];

export function useDashboardAgentTools(pageApi, { enabled = true } = {}) {
  const { tags, contactTypes, contactSources } = useCrmSettings();

  // Fresh-state refs so stable tool closures always read live values.
  const apiRef = useRef(pageApi);
  apiRef.current = pageApi;
  const settingsRef = useRef({ tags, contactTypes, contactSources });
  settingsRef.current = { tags, contactTypes, contactSources };
  const loadingRef = useRef(false);
  loadingRef.current = !!pageApi.loading;

  const settleAndCount = useCallback(async () => {
    await waitForIdle(loadingRef);
    return apiRef.current.totalCount;
  }, []);

  const applySearchEverywhere = useCallback((term) => {
    const api = apiRef.current;
    // Three-way sync: input box, committed page state, and the table hook.
    api.setTableSearchTerm(term);
    api.setActiveSearchTerm(term);
    api.setGlobalSearch(term);
  }, []);

  const viewState = useCallback(() => {
    const api = apiRef.current;
    const selectedList = api.smartLists.find((l) => l.id === api.selectedListId) || null;
    return {
      filters: api.filters || [],
      filtersSummary: summarizeConditions(api.filters),
      searchTerm: api.activeSearchTerm || '',
      sort: api.sort || null,
      totalCount: api.totalCount,
      page: api.currentPage,
      pageSize: api.pageSize,
      ownerScope: api.isCRMAdmin
        ? { selectedUserId: api.selectedUserId || '', viewing: api.selectedUserId ? (api.crmUsers.find((u) => u.uid === api.selectedUserId)?.name || api.selectedUserId) : 'all users' }
        : { viewing: 'own contacts (non-admin, fixed)' },
      selectedSmartList: describeSmartList(selectedList, api.currentUser?.uid),
      availableSmartLists: api.smartLists.map((l) => ({ id: l.id, name: l.name, isGlobal: !!l.isGlobal, isDefault: !!l.isDefault })),
    };
  }, []);

  // -------------------------------------------------------------------------
  // Tool implementations
  // -------------------------------------------------------------------------

  const tools = useMemo(() => ({
    get_view_state: () => viewState(),

    set_filters: async (args) => {
      const api = apiRef.current;
      const filters = Array.isArray(args?.filters) ? args.filters : [];
      api.applyFilters(filters);
      const totalCount = await settleAndCount();
      const result = { ok: true, appliedFilters: summarizeConditions(filters), totalCount };
      if (apiRef.current.selectedListId) {
        result.note = 'A smartlist is still selected in the UI but the on-screen filters have now drifted from it.';
      }
      return result;
    },

    clear_filters: async () => {
      const api = apiRef.current;
      api.applyFilters([]);
      applySearchEverywhere('');
      api.setSelectedListId(null);
      const totalCount = await settleAndCount();
      return { ok: true, totalCount, note: 'All filters and search cleared.' };
    },

    set_search: async (args) => {
      const term = typeof args?.searchTerm === 'string' ? args.searchTerm : '';
      applySearchEverywhere(term);
      const totalCount = await settleAndCount();
      return { ok: true, searchTerm: term, totalCount };
    },

    set_sort: async (args) => {
      const { field, direction } = args || {};
      if (!field || !['asc', 'desc'].includes(direction)) {
        return { error: 'set_sort needs field and direction ("asc"|"desc").' };
      }
      apiRef.current.applySort(field, direction);
      await waitForIdle(loadingRef);
      return { ok: true, sort: { field, direction } };
    },

    set_owner_scope: async (args) => {
      const api = apiRef.current;
      if (!api.isCRMAdmin) {
        return { error: 'Only CRM admins can change the owner scope. This user always sees their own contacts.' };
      }
      const userId = typeof args?.userId === 'string' ? args.userId : '';
      if (userId && !api.crmUsers.some((u) => u.uid === userId)) {
        return { error: `Unknown userId "${userId}". Use an id from the users reference data, or "" for all users.` };
      }
      api.setSelectedUserId(userId);
      const totalCount = await settleAndCount();
      const viewing = userId ? (api.crmUsers.find((u) => u.uid === userId)?.name || userId) : 'all users';
      return { ok: true, viewing, totalCount };
    },

    list_smart_lists: () => {
      const api = apiRef.current;
      return {
        smartLists: api.smartLists.map((l) => describeSmartList(l, api.currentUser?.uid)),
        selectedSmartListId: api.selectedListId || null,
      };
    },

    apply_smart_list: async (args) => {
      const api = apiRef.current;
      const { list, error } = resolveSmartList(api.smartLists, args || {});
      if (error) return { error };
      api.setSelectedListId(list.id);
      api.applyFilters(list.filters || []);
      const totalCount = await settleAndCount();
      return { ok: true, applied: describeSmartList(list, api.currentUser?.uid), totalCount };
    },

    create_smart_list: async (args) => {
      const api = apiRef.current;
      const { name, filters, isGlobal, setAsDefault } = args || {};
      if (isGlobal && !api.isCRMAdmin) {
        return { error: 'Only CRM admins can create global (company-wide) smartlists. Create it as personal instead.' };
      }
      const clash = api.smartLists.find((l) => (l.name || '').trim().toLowerCase() === String(name).trim().toLowerCase());
      if (clash) {
        return { error: `A smartlist named "${clash.name}" already exists (id: ${clash.id}). Use update_smart_list, or pick another name.` };
      }
      const filtersToSave = Array.isArray(filters) ? filters : (api.filters || []);
      try {
        const newList = await api.createSmartList({
          name: String(name).trim(),
          filters: filtersToSave,
          isGlobal: !!isGlobal,
          sortBy: api.sort?.field,
          sortDirection: api.sort?.direction,
        });
        api.setSelectedListId(newList.id);
        if (setAsDefault) await api.setAsDefault(newList.id);
        return {
          ok: true,
          smartListId: newList.id,
          name: newList.name,
          savedFilters: summarizeConditions(filtersToSave),
          isGlobal: !!isGlobal,
          isDefault: !!setAsDefault,
        };
      } catch (err) {
        return { error: `Could not save the smartlist: ${err.message}` };
      }
    },

    update_smart_list: async (args) => {
      const api = apiRef.current;
      const { list, error } = resolveSmartList(api.smartLists, args || {});
      if (error) return { error };
      if (!canManageSmartList(list, { userId: api.currentUser?.uid, isCRMAdmin: api.isCRMAdmin })) {
        return { error: `You can only edit your own smartlists${api.isCRMAdmin ? ' or global ones' : ''}. "${list.name}" belongs to someone else.` };
      }
      const { newName, filters, useCurrentFilters, isGlobal } = args || {};
      if (isGlobal !== undefined && isGlobal !== list.isGlobal && !api.isCRMAdmin) {
        return { error: 'Only CRM admins can change whether a smartlist is global.' };
      }
      const updates = {};
      if (newName) updates.name = String(newName).trim();
      if (useCurrentFilters) updates.filters = api.filters || [];
      else if (Array.isArray(filters)) updates.filters = filters;
      if (typeof isGlobal === 'boolean') updates.isGlobal = isGlobal;
      if (Object.keys(updates).length === 0) {
        return { error: 'Nothing to update — pass newName, filters, useCurrentFilters, or isGlobal.' };
      }
      try {
        const updated = await api.updateSmartList(list.id, updates);
        // Keep the screen consistent if the selected list's filters changed.
        if (updates.filters && api.selectedListId === list.id && !useCurrentFilters) {
          api.applyFilters(updates.filters);
          await waitForIdle(loadingRef);
        }
        return { ok: true, smartList: describeSmartList(updated, api.currentUser?.uid) };
      } catch (err) {
        return { error: `Could not update the smartlist: ${err.message}` };
      }
    },

    delete_smart_list: async (args) => {
      const api = apiRef.current;
      const { list, error } = resolveSmartList(api.smartLists, args || {});
      if (error) return { error };
      if (!canManageSmartList(list, { userId: api.currentUser?.uid, isCRMAdmin: api.isCRMAdmin })) {
        return { error: `You can only delete your own smartlists${api.isCRMAdmin ? ' or global ones' : ''}. "${list.name}" belongs to someone else.` };
      }
      const wasSelected = api.selectedListId === list.id;
      try {
        await api.deleteSmartList(list.id);
        return {
          ok: true,
          deleted: list.name,
          note: wasSelected ? 'That list was selected; the current filters remain on screen but the selection was cleared.' : undefined,
        };
      } catch (err) {
        return { error: `Could not delete the smartlist: ${err.message}` };
      }
    },

    get_daily_briefing: async (args) => {
      // Off-screen read of futurebot_briefings (the DailyBriefingCard at the
      // top of this page shows the same data). Auto-builds when missing;
      // explicit regenerate only on user request.
      try {
        const call = httpsCallable(getFunctions(app, 'us-east1'), 'getDailyBriefing');
        let res = await call({ generate: args?.regenerate === true });
        if (!res.data?.briefing && args?.regenerate !== true) {
          res = await call({ generate: true }); // first briefing of the day
        }
        const briefing = res.data?.briefing;
        if (!briefing) {
          return { error: 'No briefing could be generated for today.' };
        }
        const sections = briefing.sections || {};
        return {
          date: briefing.briefing_date,
          narrative: briefing.narrative,
          counts: sections.counts || {},
          callList: sections.callList || [],
          topRefiOpportunities: sections.refiOpportunities || [],
          wins: (sections.wins || []).map((w) => ({ name: w.name, amount: w.amount })),
          regenerated: args?.regenerate === true,
        };
      } catch (err) {
        return { error: `Could not load the briefing: ${err.message}` };
      }
    },
  }), [viewState, settleAndCount, applySearchEverywhere]);

  // -------------------------------------------------------------------------
  // Page context for the system prompt
  // -------------------------------------------------------------------------

  const getContext = useCallback(async () => {
    const api = apiRef.current;
    const { tags: tagList, contactTypes: typeList, contactSources: sourceList } = settingsRef.current;

    const state = viewState();
    const referenceData = {
      tags: (tagList || []).map((t) => t.value),
      contactTypes: (typeList || []).map((t) => t.value),
      sources: (sourceList || []).map((s) => (s && s.value !== undefined ? s.value : String(s))),
      smartLists: api.smartLists.map((l) => ({ id: l.id, name: l.name, isGlobal: !!l.isGlobal, isDefault: !!l.isDefault })),
    };
    if (api.isCRMAdmin) {
      referenceData.users = (api.crmUsers || []).map((u) => ({ id: u.uid, name: u.name }));
    }

    const scopeLabel = api.isCRMAdmin ? `Owner scope: ${state.ownerScope.viewing}.` : 'Scoped to own contacts.';
    return {
      referenceData,
      stateSummary:
        `Contacts table: ${state.totalCount ?? '?'} result(s), page ${state.page}. ` +
        `Filters: ${state.filtersSummary}. Search: ${state.searchTerm ? `"${state.searchTerm}"` : 'none'}. ${scopeLabel}` +
        (state.selectedSmartList ? ` Selected smartlist: "${state.selectedSmartList.name}".` : ''),
    };
  }, [viewState]);

  const registration = useMemo(() => {
    if (!enabled) return null;
    return {
      pageId: 'crm-dashboard',
      title: 'Origin Dashboard',
      tools,
      getContext,
      suggestions: DASHBOARD_SUGGESTIONS,
    };
  }, [enabled, tools, getContext]);

  return { registration };
}

export default useDashboardAgentTools;
